/* /src/mitras/RegionPicker.jsx
 *
 * Bharat-first state → district picker. Used by:
 *   • /professional/register (Mitra onboarding)
 *   • Admin Mitra moderation panel filter
 *   • Future /mitras/:bucket/:state/:district SEO routes
 *
 * Props:
 *   • stateCode (string) — controlled selected state code, e.g. "IN-UP"
 *   • district (string) — controlled selected district name
 *   • onChange ({state_code, state_name, district}) — called whenever
 *     the user changes either dropdown
 *   • required (bool) — adds aria-required to selects
 *
 * Behaviour:
 *   • Reset district when state changes
 *   • Plain native <select> (works on every Tier-3 phone, supports
 *     OS-native search). No third-party combobox.
 */
import React from "react";
import { listStates, listDistricts, findState } from "@/mitras/regions";

const RegionPicker = ({ stateCode, district, onChange, required = false, testIdPrefix = "region-picker" }) => {
  const states = listStates();
  const districts = listDistricts(stateCode);
  const stateMeta = findState(stateCode);

  const handleState = (code) => {
    const meta = findState(code);
    onChange?.({
      state_code: code,
      state_name: meta?.name || "",
      district: "", // reset when state changes
    });
  };

  const handleDistrict = (d) => {
    onChange?.({
      state_code: stateCode,
      state_name: stateMeta?.name || "",
      district: d,
    });
  };

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      data-testid={testIdPrefix}
    >
      <label className="block">
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">
          State <span className="text-gold-dark">*</span>
        </span>
        <select
          value={stateCode || ""}
          onChange={(e) => handleState(e.target.value)}
          required={required}
          aria-required={required}
          data-testid={`${testIdPrefix}-state`}
          className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy font-bold"
        >
          <option value="">Select your state</option>
          {states.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.name_hi})
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">
          District {required && <span className="text-gold-dark">*</span>}
        </span>
        <select
          value={district || ""}
          onChange={(e) => handleDistrict(e.target.value)}
          disabled={!stateCode}
          required={required}
          aria-required={required}
          data-testid={`${testIdPrefix}-district`}
          className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {stateCode ? "Select your district" : "Select state first"}
          </option>
          {districts.map((d) => (
            <option key={d.slug} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

export default RegionPicker;
